import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { BASIC_YELLOW } from "../constants/colors";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  GOOGLE_MAPS_API_KEY,
} from "../constants/map";
import { PageRoutes } from "../constants/routes";
import { appActions } from "../ducks/app";
import { StoreSearchType } from "../ducks/store";
import { useNavigation } from "./useNavigation";
import { useStore } from "./useStore";
import { useWOVN } from "./useWOVN";

interface GoogleMapWindow extends Window {
  handleLoadGoogleMapsScript: () => void;
  handleClickStore: (storeAddress: string) => void;
  google: {
    maps: {
      Map: any;
      LatLng: any;
      Marker: any;
      InfoWindow: any;
      LatLngBounds: any;
      MapTypeId: any;
      event: {
        addListener: (marker: any, event: string, callback: () => void) => void;
      };
    };
  };
}

declare const window: GoogleMapWindow;

export const useMap = () => {
  const dispatch = useDispatch();
  const { navigateToPage } = useNavigation();
  const { getTextTranslation } = useWOVN();

  const { allStores } = useStore([]);

  const setCurrentPosition = useCallback(
    (map: any, position: GeolocationPosition, infoWindow: any) => {
      const google = window.google;

      map.setCenter(
        new google.maps.LatLng(
          position.coords.latitude,
          position.coords.longitude,
        ),
      );

      const currentPositionMarker = new google.maps.Marker({
        position: new google.maps.LatLng(
          position.coords.latitude,
          position.coords.longitude,
        ),
        map,
        visible: true,
      });
      google.maps.event.addListener(currentPositionMarker, "click", () => {
        infoWindow.setContent(
          '<p style="padding: 0px 20px 35px 20px; margin: 0;">現在位置</p>',
        );
        infoWindow.open(map, currentPositionMarker);
      });
    },
    [],
  );

  const setStoreMarkers = useCallback(
    (map: any, bounds: any, infoWindow: any) => {
      allStores.forEach((store) => {
        const google = window.google;
        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(
            store.location.latitude,
            store.location.longitude,
          ),
          map,
          visible: true,
          icon: require("../assets/icons/icon-pin2.png"),
        });
        bounds.extend({
          lat: store.location.latitude,
          lng: store.location.longitude,
        });
        google.maps.event.addListener(marker, "click", () => {
          const translatedStoreName = getTextTranslation(store.name);
          infoWindow.setContent(`\
<div style="padding: 0px 20px 35px 20px; margin: 0;">\
  <div\
    style="font-size:16px; color: ${BASIC_YELLOW}; cursor: pointer;"\
    onmouseover="this.style.textDecoration='underline'"\
    onmouseout="this.style.textDecoration='none'"\
    onclick="handleClickStore('${store.prefecture + store.city + store.address}')"\
  >\
    ${translatedStoreName}\
  </div>\
</div>\
`);
          infoWindow.open(map, marker);
        });
      });
    },
    [allStores],
  );

  useEffect(() => {
    if (allStores.length === 0) return;

    window.handleClickStore = (storeAddress: string) => {
      navigateToPage(
        PageRoutes.SHOP_SEARCH.replace(
          ":type",
          StoreSearchType.Text.valueOf(),
        ).replace(":query", storeAddress),
      );
    };

    window.handleLoadGoogleMapsScript = () => {
      const google = window.google;
      const map = new google.maps.Map(document.getElementById("googleMap"), {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: true,
        zoomControl: true,
        scaleControl: true,
      });

      const bounds = new google.maps.LatLngBounds();
      const infoWindow = new google.maps.InfoWindow();

      setStoreMarkers(map, bounds, infoWindow);

      // HTML5 geolocation.
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            setCurrentPosition(map, position, infoWindow);
          },
          (err) => {
            if (err.code === 1 || err.code === 2) {
              dispatch(
                appActions.showModal.started({
                  title: "位置情報がオフになっています",
                  subTitle:
                    "位置情報をオンにして、ブラウザを再読み込みしてください。",
                  nextButtonLabel: "閉じる",
                  handleNext: () => dispatch(appActions.closeModal()),
                }),
              );
            } else {
              dispatch(
                appActions.showModal.started({
                  title: "位置情報が取得できませんでした",
                  subTitle: "時間を置いてから、再度お試しください。",
                  nextButtonLabel: "閉じる",
                  handleNext: () => dispatch(appActions.closeModal()),
                }),
              );
            }
          },
        );
      } else {
        console.log("Browser doesn't support Geolocation");
      }
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&callback=handleLoadGoogleMapsScript`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [allStores]);

  return {};
};
